import { useApolloClient, useMutation } from "@apollo/client";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import { ChangeEvent, FC, useState, KeyboardEvent } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { SIGNUP_PROGRESSION } from "../../../graphql/account/mutation";
import {
  SignupProgression,
  SignupProgressionVariables,
} from "../../../graphql/account/types/SignupProgression";
import Address from "../../Common/Address/Address";
import { signupProgression } from "../../../enums/account";
import { expectedMonthlyExpenses } from "../../../constants/expectedExpenses";
import { sortList } from "../../../utils/selectItems";
import { SapheerError } from "../../../enums/error";
import FileUploadLinkcy from "../../Common/FileUpload/FileUploadLinkcy";
import { FileInterfaceLinkcy } from "../../../interface/FileInterface";
import { fileToBase64 } from "../../../utils/fileUtils";
import { DocumentTypeInfo } from "../../../graphql/onboarding/document/types/DocumentTypeInfo";
import { DocumentsType_documentsType } from "../../../graphql/onboarding/document/types/DocumentsType";
import { CreateCompanyInput } from "../../../types/graphql-global-types-onboarding";
import { convertListToItems } from "../../../utils/Items";
import { ACCOUNT_PURPOSES } from "../../../constants/accountPurposes";
import { DocumentType } from "../../../enums/document";
import CustomTextField from "../../Ui/CustomTextField";
import CustomSelect from "../../Ui/CustomSelect";
import CustomDate from "../../Ui/CustomDate";
import { CustomButton } from "../../Ui/CustomButton";
import { useToastNotifications } from "../../../utils/toastUtils";
import {
  CreateCompany,
  CreateCompanyVariables,
} from "../../../graphql/onboarding/company/types/CreateCompany";
import { CREATE_COMPANY } from "../../../graphql/onboarding/company/mutation";
import {
  fetchLegalFormLabel,
  individualLegalForm,
  isIndividualLegalForm,
  legalForm,
} from "../../../constants/legalForm";

const styles = makeStyles((theme) => ({
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(2, 0),
  },
  box: {
    display: "flex",
    flexDirection: "row",
    gap: 10,
    "@media(max-width:935px)": {
      flexDirection: "column",
    },
  },
  boxRow: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: 450,
  },
}));

interface CompanyInfoProps {
  previous: string;
  disable: boolean;
  corporateSaved: CreateCompanyInput;
  documentsType: (DocumentsType_documentsType | null)[];
  isAutocomplete: boolean;
}

const CompanyInfo: FC<CompanyInfoProps> = (props) => {
  const classes = styles();
  const client = useApolloClient();
  const navigate = useNavigate();
  const intl = useIntl();

  const { disable, corporateSaved, previous, documentsType, isAutocomplete } =
    props;
  const [corporate, setCorporate] =
    useState<CreateCompanyInput>(corporateSaved);
  const [loading, setLoading] = useState(false);

  const { showErrorToast } = useToastNotifications();
  const [finishStep, { loading: finishStepLoading }] = useMutation<
    SignupProgression,
    SignupProgressionVariables
  >(SIGNUP_PROGRESSION, {
    onCompleted: () => {
      setLoading(false);
    },
  });

  const passNext = () => {
    finishStep({
      variables: {
        step: signupProgression.LEADER,
      },
    });
    setLoading(false);
    navigate("/manager", { replace: true });
  };

  const [createCompany, { loading: createLoading }] = useMutation<
    CreateCompany,
    CreateCompanyVariables
  >(CREATE_COMPANY, {
    onCompleted: () => {
      passNext();
    },
    onError: (error) => {
      setLoading(false);
      const { graphQLErrors, networkError } = error;
      let message = "create.corporate.error";
      if (networkError) {
        message = "network.error";
      } else if (
        graphQLErrors?.[0]?.message === SapheerError.INCORPORATION_ALREADY_EXIST
      ) {
        message = "create.corporate.already.exists";
      }
      showErrorToast({ description: message });
    },
  });

  const handlePrevious = () => {
    navigate(previous, { replace: true });
  };

  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleNext = () => {
    setIsSubmitted(true);
    const {
      city,
      address,
      code,
      countryIsoCode,
      legalAddress,
      legalCity,
      legalCountryIsoCode,
      legalPostcode,
      name,
      postcode,
      registrationDate,
      legalFormId,
      leiCode,
      taxpayerIdentificationNumber,
      accountPurposes,
      expectedMonthlyExpensesValue,
    } = corporate;

    if (!name) {
      return showErrorToast({ description: "corporate.registeredName.empty" });
    }
    if (!taxpayerIdentificationNumber) {
      return showErrorToast({
        description: "corporate.incorporationCode.empty",
      });
    }
    if (!legalFormId) {
      return showErrorToast({ description: "corporate.legalType.empty" });
    }
    if (!countryIsoCode) {
      return showErrorToast({
        description: "corporate.incorporationCountry.empty",
      });
    }
    if (!registrationDate) {
      return showErrorToast({
        description: "corporate.incorporationDate.empty",
      });
    }
    if (!legalAddress || !address) {
      return showErrorToast({ description: "address.street.empty" });
    }
    if (!legalCountryIsoCode) {
      return showErrorToast({ description: "address.isoCountryCode.empty" });
    }
    if (!legalPostcode || !postcode) {
      return showErrorToast({ description: "address.postalCode.empty" });
    }
    if (!legalCity || !city) {
      return showErrorToast({ description: "address.city.empty" });
    }
    if (!accountPurposes) {
      return showErrorToast({ description: "corporate.accountPurposes.empty" });
    }
    if (!expectedMonthlyExpensesValue) {
      return showErrorToast({
        description: "corporate.expectedMonthlyExpensesValue.empty",
      });
    }

    const input: CreateCompanyVariables["input"] = {
      city,
      address,
      code,
      countryIsoCode,
      countryTaxResidenceIsoCode: countryIsoCode,
      legalAddress,
      legalCity,
      legalCountryIsoCode,
      legalPostcode,
      name,
      postcode,
      registrationDate,
      legalFormId,
      legalFormLabel: fetchLegalFormLabel(legalFormId),
      leiCode,
      taxpayerIdentificationNumber,
      isIndividual: isIndividualLegalForm(legalFormId),
      documents: corporate.documents || [],
      isAutocomplete,
      accountPurposes,
      expectedMonthlyExpensesValue,
    };

    createCompany({
      variables: {
        input,
      },
    });

    setLoading(true);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleNext();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCorporate({ ...corporate, [event.target.name]: event.target.value });
  };

  const handleSelect = (name: string, value: string) => {
    setCorporate({
      ...corporate,
      [name]: value,
    });
  };

  const handleDateChange = (name: string, date: any) => {
    setCorporate({ ...corporate, [name]: date });
  };

  const handleUpload = async (
    documentType: DocumentTypeInfo,
    files: FileInterfaceLinkcy[],
  ) => {
    const uploadPromises = files.map(async (file: FileInterfaceLinkcy) => {
      const base64File = await fileToBase64(file.file as unknown as File);
      return {
        type: documentType.type,
        base64Content: base64File,
        filename: file?.filename ?? file?.file?.name ?? "file",
        fileType: file?.filetype ?? file?.file?.type,
      };
    });
    Promise.all(uploadPromises)
      .then((newDocuments) => {
        if (newDocuments?.[0]) {
          const newDoc = newDocuments[0];
          setCorporate((prevCorporate) => ({
            ...prevCorporate,
            documents: [
              ...prevCorporate?.documents?.filter(
                (doc) => doc && doc?.type !== newDoc.type,
              )!,
              newDoc,
            ],
          }));
        }
      })
      .catch((error) => {
        showErrorToast({ description: "file.upload.error" });
      });
  };

  const handleRemoveFile = (type: string) => {
    setCorporate((prevCorporate) => ({
      ...prevCorporate,
      documents: [
        ...prevCorporate?.documents?.filter((doc) => doc && doc?.type !== type),
      ],
    }));
  };

  return (
    <>
      <form onKeyPress={handleKeyPress}>
        <Box className={classes.box}>
          <Box className={classes.boxRow}>
            <CustomTextField
              name="name"
              label={intl.formatMessage({ id: "stepFour.legalName" })}
              placeholder="Nom de l'entreprise"
              onChange={handleChange}
              value={corporate.name || ""}
              isSubmitted={isSubmitted}
              disabled={disable}
            />
            <CustomTextField
              name="code"
              label={intl.formatMessage({
                id: "corporate.incorporationCode",
              })}
              onChange={handleChange}
              value={corporate.code}
              isSubmitted={isSubmitted}
              disabled={disable}
            />
            <CustomSelect
              setValue={handleSelect}
              options={sortList(legalForm)}
              name="legalFormId"
              value={corporate.legalFormId}
              disabled={disable}
              isSubmitted={isSubmitted}
            />
            <CustomDate
              label={intl.formatMessage({
                id: "corporate.incorporationDate",
              })}
              name="registrationDate"
              handleDateChange={handleDateChange}
              errorMessageId="maxdate.message"
              isSubmitted={isSubmitted}
              type="date"
              value={corporate.registrationDate}
              maxDate={new Date()}
            />
            <Address
              information={corporate}
              handleInputChange={handleChange}
              disable={disable}
              isCompany
              handleSelect={handleSelect}
              isSubmitted={isSubmitted}
              isLegal
            />
          </Box>
          <Box className={classes.boxRow}>
            {false && (
              <CustomTextField
                name="leiCode"
                label={"Code LEI"}
                onChange={handleChange}
                value={corporate?.leiCode || ""}
                isSubmitted={isSubmitted}
                disabled={disable}
              />
            )}
            <Address
              information={corporate}
              handleInputChange={handleChange}
              disable={disable}
              isCompany
              handleSelect={handleSelect}
              isSubmitted={isSubmitted}
            />
            <CustomTextField
              name="taxpayerIdentificationNumber"
              label={intl.formatMessage({ id: "corporate.vatNumber" })}
              placeholder="TVA..."
              onChange={handleChange}
              value={corporate.taxpayerIdentificationNumber || ""}
              isSubmitted={isSubmitted}
              disabled={disable}
            />
            {/* <CustomSelect
              setValue={handleSelect}
              options={GetListSelect(INDUSTRY_FR, INDUSTRY_EN)}
              name="industry"
              value={corporate?.industry}
              disabled={disable}
              isSubmitted={isSubmitted}
              label={intl.formatMessage({ id: "corporate.industry" })}
            /> */}
            <CustomSelect
              setValue={handleSelect}
              options={convertListToItems(ACCOUNT_PURPOSES, intl)}
              name="accountPurposes"
              value={corporate?.accountPurposes}
              disabled={disable}
              isSubmitted={isSubmitted}
              label={intl.formatMessage({
                id: "corporate.accountPurposes",
              })}
            />
            {/* <CustomSelect
              setValue={handleSelect}
              options={employeesRange}
              name="employeesRange"
              value={corporate?.employeesRange}
              disabled={disable}
              isSubmitted={isSubmitted}
              label={intl.formatMessage({
                id: "corporate.employeesRange",
              })}
            /> */}
            <CustomSelect
              setValue={handleSelect}
              options={expectedMonthlyExpenses}
              name="expectedMonthlyExpensesValue"
              value={corporate?.expectedMonthlyExpensesValue}
              disabled={disable}
              isSubmitted={isSubmitted}
              label={`${intl.formatMessage({
                id: "corporate.expectedMonthlyExpenses",
              })} €`}
            />

            {documentsType?.length &&
              documentsType.map((docType) => {
                if (docType?.id) {
                  const category = docType?.type ?? "";
                  if (
                    category === DocumentType.ARTICLE_OF_ASSOCIATION &&
                    [...individualLegalForm]?.some(
                      (type) => type.id === corporate?.legalFormId,
                    )
                  ) {
                    return;
                  }
                  return (
                    <FileUploadLinkcy
                      key={docType.id}
                      category={category}
                      label={docType?.name ?? ""}
                      setDocument={(files) => handleUpload(docType, files)}
                      isSubmitted={isSubmitted}
                      fileData={
                        corporate?.documents?.filter(
                          (doc) => doc?.type === docType.type,
                        )[0] as unknown as undefined
                      }
                      onRemove={handleRemoveFile}
                    />
                  );
                }
              })}
          </Box>
        </Box>
      </form>
      <Box className={classes.button}>
        <CustomButton
          type="button"
          variant="outline4"
          onClick={handlePrevious}
          label="button.previous"
          disabled={loading}
          fullWidthMobile
        />
        <CustomButton
          type="button"
          variant="primary"
          onClick={handleNext}
          label="button.next"
          loading={createLoading || finishStepLoading || loading}
          fullWidthMobile
        />
      </Box>
    </>
  );
};

export default CompanyInfo;
